import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Plus,
  Truck,
  ShieldCheck,
  Search,
  ArrowUpRight,
  Pencil,
  MoreHorizontal,
  Building2,
} from 'lucide-react';
import { DataTable, type Column, type TableDensity } from '@/components/common/DataTable';
import { DensityToggle } from '@/components/common/DensityToggle';
import { ListPageHeader } from '@/components/common/ListPageHeader';
import { ErrorState } from '@/components/common/ErrorState';
import { KpiCard } from '@/components/common/KpiCard';
import { StatusTabs, type StatusTabOption } from '@/components/common/StatusTabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { vendorsService } from '@/services/vendors';
import type { Vendor } from '@/types/vendors';
import { useDebounced } from '@/hooks/useDebounced';
import { VendorFormSheet } from './VendorFormSheet';
import { VendorDetailSheet } from './VendorDetailSheet';

type StatusFilter = 'all' | 'ACTIVE' | 'INACTIVE';

export function VendorsPage() {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [selected, setSelected] = useState<Vendor | null>(null);
  const [editing, setEditing] = useState<Vendor | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [density, setDensity] = useState<TableDensity>('compact');
  const debounced = useDebounced(search);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['vendors', { q: debounced }],
    queryFn: () =>
      vendorsService.list({
        search: debounced || undefined,
        pageSize: 200,
      }),
  });

  const allRows = useMemo(() => data?.items ?? [], [data]);

  const counts = useMemo(() => {
    const c = { all: allRows.length, ACTIVE: 0, INACTIVE: 0 };
    for (const r of allRows) c[r.status] += 1;
    return c;
  }, [allRows]);

  const filtered = useMemo(() => {
    if (filter === 'all') return allRows;
    return allRows.filter((r) => r.status === filter);
  }, [allRows, filter]);

  const summary = useMemo(() => {
    const withGstin = allRows.filter((r) => !!r.gstin).length;
    const withPan = allRows.filter((r) => !!r.pan).length;
    return { active: counts.ACTIVE, withGstin, withPan };
  }, [allRows, counts.ACTIVE]);

  const tabs: StatusTabOption[] = [
    { value: 'all',      label: 'All',      count: counts.all },
    { value: 'ACTIVE',   label: 'Active',   count: counts.ACTIVE },
    { value: 'INACTIVE', label: 'Archived', count: counts.INACTIVE },
  ];

  const columns: Column<Vendor>[] = [
    {
      key: 'code',
      header: 'Code',
      width: '120px',
      cell: (r) => <span className="font-mono text-[11.5px] text-muted-foreground">{r.code}</span>,
    },
    {
      key: 'name',
      header: 'Vendor',
      cell: (r) => (
        <div className="flex items-center gap-2.5">
          <span
            aria-hidden="true"
            className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-primary/10 text-primary"
          >
            <Building2 className="h-3.5 w-3.5" />
          </span>
          <div className="min-w-0 space-y-0.5">
            <div className="truncate font-medium text-foreground">{r.name}</div>
            <div className="truncate text-[11px] text-muted-foreground">
              {r.email ?? r.phone ?? '—'}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'gstin',
      header: 'GSTIN',
      width: '170px',
      cell: (r) =>
        r.gstin ? (
          <span className="font-mono text-[11.5px]">{r.gstin}</span>
        ) : (
          <span className="text-[11.5px] text-muted-foreground">—</span>
        ),
    },
    {
      key: 'pan',
      header: 'PAN',
      width: '120px',
      cell: (r) =>
        r.pan ? (
          <span className="font-mono text-[11.5px]">{r.pan}</span>
        ) : (
          <span className="text-[11.5px] text-muted-foreground">—</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '110px',
      cell: (r) => (
        <Badge variant={r.status === 'ACTIVE' ? 'success' : 'muted'} className="text-[10px]">
          {r.status === 'ACTIVE' ? 'active' : 'archived'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '100px',
      hoverReveal: true,
      cell: (r) => (
        <div className="flex items-center justify-end gap-0.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                onClick={() => setSelected(r)}
              >
                <ArrowUpRight className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Open</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                onClick={() => setEditing(r)}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Edit vendor</TooltipContent>
          </Tooltip>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-foreground">
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setSelected(r)}>Open detail</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setEditing(r)}>Edit vendor</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-4">
        <ListPageHeader
          title="Vendors"
          count={counts.all}
          actions={
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="h-3.5 w-3.5" />
              New vendor
            </Button>
          }
        />

        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          <KpiCard label="Active" value={summary.active.toString()} tone="primary" icon={Truck} />
          <KpiCard label="With GSTIN" value={summary.withGstin.toString()} tone="default" icon={ShieldCheck} />
          <KpiCard label="With PAN" value={summary.withPan.toString()} tone="default" icon={ShieldCheck} />
        </div>

        <StatusTabs value={filter} onChange={(v) => setFilter(v as StatusFilter)} options={tabs} />

        <div className="flex items-center justify-between gap-3">
          <div className="relative w-full max-w-sm">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, code, email, GSTIN…"
              className="h-8 pl-8 text-[13px]"
            />
          </div>
          <DensityToggle value={density} onChange={setDensity} />
        </div>

        {error ? (
          <ErrorState error={error} onRetry={() => refetch()} />
        ) : (
          <DataTable
            columns={columns}
            rows={filtered}
            rowKey={(r) => r.id}
            isLoading={isLoading}
            onRowClick={(r) => setSelected(r)}
            density={density}
            emptyTitle={
              filter === 'all' ? 'No vendors yet' : `No ${filter.toLowerCase()} vendors`
            }
            emptyDescription={
              filter === 'all'
                ? 'Add your first vendor to start logging purchase orders + supplier deliveries.'
                : 'Try a different status filter.'
            }
            emptyAction={
              filter === 'all' ? (
                <Button size="sm" onClick={() => setCreateOpen(true)}>
                  <Plus className="h-3.5 w-3.5" />
                  New vendor
                </Button>
              ) : null
            }
          />
        )}

        <VendorDetailSheet
          vendor={selected}
          open={!!selected}
          onOpenChange={(o) => !o && setSelected(null)}
          onEdit={(v) => {
            setSelected(null);
            setEditing(v);
          }}
        />

        <VendorFormSheet vendor={null} open={createOpen} onOpenChange={setCreateOpen} />
        <VendorFormSheet
          vendor={editing}
          open={!!editing}
          onOpenChange={(o) => !o && setEditing(null)}
        />
      </div>
    </TooltipProvider>
  );
}
